import React from 'react'
import PropTypes from 'prop-types'
import {
  AddGoalsCopy,
  AddGoalsWrapper,
  CheckboxWrapper,
  CheckIconWrapper,
  DateTitle,
  DateWrapper,
  Error,
  ErrorText,
  HiddenCheckbox,
  InputsWrapper,
  OptionalGoalsSection,
  NumberError,
  NumberTitle,
  NumberWrapper,
  PreviousYearText,
  StyledCalendarInput,
  StyledCheckbox,
  StyledCheckIcon,
  StyledFormInputNumber,
  StyledHr,
  StyledWarningIcon,
} from './component.styles'
import { StyledHeader } from '../component.styles'
import RequiredGoal from '../RequiredGoal'
import OptionalGoals from '../OptionalGoals'
import {
  classCreationGoalValueName,
  classCreationGoalDateName,
  lessonCompletionGoalValueName,
  lessonCompletionGoalDateName,
  programStartDateName,
  schoolwideGoalIsDisabledName,
} from './data.js'

const AddGoals = ({
  additionalGoalCount,
  modalData,
  values,
  errors,
  touched,
  handleChange,
  handleChangeGoalCount,
}) => {
  values.classCreationGoalPreviousYear = 40
  values.classesCreatedPreviousYear = 30
  values.lessonCompletionGoalPreviousYear = 80
  values.lessonCompletionPercentagePreviousYear = 86
  const { bodyCopy, choiceContent, header } = modalData || {}
  const [classCreationData, lessonCompletionData] = choiceContent || []

  return (
    <AddGoalsWrapper>
      <StyledHeader>{header}</StyledHeader>
      <AddGoalsCopy>{bodyCopy}</AddGoalsCopy>
      <RequiredGoal
        dataTestId={'class-creation-goal'}
        description={classCreationData.bodyCopy}
        title={classCreationData.h3}
      >
        <InputsWrapper>
          <NumberWrapper>
            <NumberTitle>Number of Classes to Create</NumberTitle>
            <StyledFormInputNumber
              dataTestId={classCreationGoalValueName}
              error={
                touched[classCreationGoalValueName] &&
                errors[classCreationGoalValueName]
              }
              onChange={value => {
                handleChange(classCreationGoalValueName, value)
              }}
              placeholder="Enter number of classes"
              value={values[classCreationGoalValueName]}
            />
            {values.classesCreatedPreviousYear &&
              values.classCreationGoalPreviousYear && (
                <PreviousYearText>{`2024-25 classes created: ${values.classesCreatedPreviousYear} | Goal: ${values.classCreationGoalPreviousYear}`}</PreviousYearText>
              )}
            {touched[classCreationGoalValueName] &&
              errors[classCreationGoalValueName] && (
                <NumberError>
                  <StyledWarningIcon />
                  <ErrorText>{errors[classCreationGoalValueName]}</ErrorText>
                </NumberError>
              )}
          </NumberWrapper>
          <DateWrapper>
            <DateTitle>Target Date</DateTitle>
            <StyledCalendarInput
              date={values[classCreationGoalDateName]}
              handleChange={handleChange}
              hasError={
                !!errors[classCreationGoalDateName] &&
                !!touched[classCreationGoalDateName]
              }
              name={classCreationGoalDateName}
              showNeighboringMonth={false}
            />
            {errors[classCreationGoalDateName] &&
              touched[classCreationGoalDateName] && (
                <Error>
                  <StyledWarningIcon />
                  <ErrorText>{errors[classCreationGoalDateName]}</ErrorText>
                </Error>
              )}
          </DateWrapper>
        </InputsWrapper>
      </RequiredGoal>

      <RequiredGoal
        dataTestId={'lesson-completion-goal'}
        description={lessonCompletionData.bodyCopy}
        recommendation={'60%-80% Recommended'}
        title={lessonCompletionData.h3}
      >
        <InputsWrapper>
          <NumberWrapper>
            <NumberTitle>% of Lessons Completed</NumberTitle>
            <StyledFormInputNumber
              dataTestId={lessonCompletionGoalValueName}
              disabled={values.schoolwideGoalIsDisabled}
              error={
                touched[lessonCompletionGoalValueName] &&
                errors[lessonCompletionGoalValueName]
              }
              onChange={value => {
                handleChange(lessonCompletionGoalValueName, value)
              }}
              placeholder="Enter number"
              value={values[lessonCompletionGoalValueName]}
            />
            {values.lessonCompletionPercentagePreviousYear &&
              values.lessonCompletionPercentagePreviousYear && (
                <PreviousYearText>{`2024-25 completion: ${values.lessonCompletionPercentagePreviousYear}% | Goal: ${values.lessonCompletionGoalPreviousYear}%`}</PreviousYearText>
              )}
            {touched[lessonCompletionGoalValueName] &&
              errors[lessonCompletionGoalValueName] && (
                <NumberError>
                  <StyledWarningIcon />
                  <ErrorText>{errors[lessonCompletionGoalValueName]}</ErrorText>
                </NumberError>
              )}
          </NumberWrapper>
          <DateWrapper>
            <DateTitle>Program Start Date</DateTitle>
            <StyledCalendarInput
              date={values[programStartDateName]}
              disabled={values.schoolwideGoalIsDisabled}
              error={errors?.[programStartDateName]}
              handleChange={handleChange}
              hasError={
                !!errors[programStartDateName] &&
                !!touched[programStartDateName]
              }
              name={programStartDateName}
              showNeighboringMonth={false}
            />
            {errors[programStartDateName] && touched[programStartDateName] && (
              <Error>
                <StyledWarningIcon />
                <ErrorText>{errors[programStartDateName]}</ErrorText>
              </Error>
            )}
          </DateWrapper>
          <DateWrapper>
            <DateTitle>Target Date</DateTitle>
            <StyledCalendarInput
              date={values[lessonCompletionGoalDateName]}
              disabled={values.schoolwideGoalIsDisabled}
              error={errors?.[lessonCompletionGoalDateName]}
              handleChange={handleChange}
              hasError={
                !!errors[lessonCompletionGoalDateName] &&
                !!touched[lessonCompletionGoalDateName]
              }
              name={lessonCompletionGoalDateName}
              showNeighboringMonth={false}
            />
            {errors[lessonCompletionGoalDateName] &&
              touched[lessonCompletionGoalDateName] && (
                <Error>
                  <StyledWarningIcon />
                  <ErrorText>{errors[lessonCompletionGoalDateName]}</ErrorText>
                </Error>
              )}
          </DateWrapper>
        </InputsWrapper>
        <StyledHr />
        <CheckboxWrapper>
          <HiddenCheckbox
            checked={values.schoolwideGoalIsDisabled}
            id={schoolwideGoalIsDisabledName}
            name={schoolwideGoalIsDisabledName}
            onChange={e => {
              handleChange(schoolwideGoalIsDisabledName, e.target.checked)
              if (
                !values.schoolwideGoalIsDisabled &&
                !values.goal3Text &&
                !values.goal3Date
              ) {
                handleChangeGoalCount(1)
              }
              if (
                values.schoolwideGoalIsDisabled &&
                !values.goal3Text &&
                !values.goal3Date &&
                additionalGoalCount > 0
              ) {
                handleChangeGoalCount(-1)
              }
            }}
            type="checkbox"
          />
          <StyledCheckbox htmlFor={schoolwideGoalIsDisabledName}>
            <CheckIconWrapper>
              <StyledCheckIcon />
            </CheckIconWrapper>
          </StyledCheckbox>
          <AddGoalsCopy>
            Setting a schoolwide goal isn't relevant for my school
          </AddGoalsCopy>
        </CheckboxWrapper>
      </RequiredGoal>

      <OptionalGoalsSection>
        <OptionalGoals
          additionalGoalCount={additionalGoalCount}
          errors={errors}
          handleChange={handleChange}
          handleChangeGoalCount={handleChangeGoalCount}
          touched={touched}
          values={values}
        />
      </OptionalGoalsSection>
    </AddGoalsWrapper>
  )
}

AddGoals.propTypes = {
  additionalGoalCount: PropTypes.number,
  errors: PropTypes.object,
  handleChange: PropTypes.func,
  handleChangeGoalCount: PropTypes.func,
  hasApiError: PropTypes.bool,
  isValid: PropTypes.bool,
  modalData: PropTypes.object,
  touched: PropTypes.object,
  values: PropTypes.object,
}

export default AddGoals
